import React, { useState } from 'react';
import { View, TouchableOpacity, Modal, Platform, ToastAndroid } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from '../../hooks/useTranslation';
import { Language } from '../../i18n';

interface LanguageSelectorProps {
  variant?: 'compact' | 'button';
  showLabel?: boolean;
}

const LANGUAGE_OPTIONS = [
  { code: 'en' as Language, name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'tr' as Language, name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷' },
];

export function LanguageSelector({ variant = 'compact', showLabel = false }: LanguageSelectorProps) {
  const { language, changeLanguage } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [feedbackVisible, setFeedbackVisible] = useState(false);

  const currentLanguage = LANGUAGE_OPTIONS.find((lang) => lang.code === language) || LANGUAGE_OPTIONS[0];

  const handleLanguageSelect = async (lang: Language) => {
    if (lang === language) {
      setModalVisible(false);
      return;
    }

    setIsChanging(true);
    try {
      let scrollY = 0;
      if (Platform.OS === 'web') {
        try {
          scrollY = window.scrollY || window.pageYOffset || 0;
        } catch {}
      }

      await changeLanguage(lang);
      setModalVisible(false);

      if (Platform.OS === 'android') {
        try {
          ToastAndroid.show('Language updated', ToastAndroid.SHORT);
        } catch {
          setFeedbackVisible(true);
          setTimeout(() => setFeedbackVisible(false), 1200);
        }
      } else {
        setFeedbackVisible(true);
        setTimeout(() => setFeedbackVisible(false), 1200);
      }

      if (Platform.OS === 'web') {
        try {
          const y = Math.max(0, scrollY || 0);
          setTimeout(() => {
            window.scrollTo({ top: y, left: 0, behavior: 'auto' });
          }, 0);
        } catch {}
      }
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsChanging(false);
    }
  };

  const renderTrigger = () => {
    if (variant === 'compact') {
      const nonIOS = Platform.OS !== 'ios';
      return (
        <TouchableOpacity
          onPress={() => setModalVisible(true)}
          className={`flex-row items-center ${nonIOS ? 'px-3 h-9' : 'w-10 h-10'} rounded-lg bg-muted`}
          accessibilityRole="button"
          accessibilityLabel={`Current language: ${currentLanguage.nativeName}. Tap to change language`}
          accessibilityHint="Opens language selection menu"
        >
          <Text className="text-xl">{currentLanguage.flag}</Text>
          {nonIOS && (
            <Text className="ml-2 text-xs font-medium text-muted-foreground">
              {currentLanguage.code.toUpperCase()}
            </Text>
          )}
        </TouchableOpacity>
      );
    }

    return (
      <View>
        {showLabel && (
          <Text className="text-sm font-medium text-muted-foreground mb-2">
            Language
          </Text>
        )}
        <Button
          variant="outline"
          size="default"
          onPress={() => setModalVisible(true)}
          className="flex-row items-center justify-between"
        >
          <View className="flex-row items-center gap-2">
            <Text className="text-xl">{currentLanguage.flag}</Text>
            <Text className="text-base font-medium">
              {currentLanguage.nativeName}
            </Text>
          </View>
          <MaterialIcons name="arrow-drop-down" size={24} color="#6b7280" />
        </Button>
      </View>
    );
  };

  return (
    <>
      {renderTrigger()}

      <Modal
        visible={modalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setModalVisible(false)}
        statusBarTranslucent
      >
        <TouchableOpacity
          className="flex-1 bg-black/50 items-center justify-center p-6"
          activeOpacity={1}
          onPress={() => setModalVisible(false)}
        >
          <TouchableOpacity activeOpacity={1} className="w-full max-w-md">
            <Card>
              <CardContent className="p-6">
                <View className="flex-row items-center justify-between mb-4">
                  <Text className="text-xl font-semibold text-foreground">
                    Select Language
                  </Text>
                  <TouchableOpacity
                    onPress={() => setModalVisible(false)}
                    className="w-8 h-8 items-center justify-center rounded-full bg-muted"
                    accessibilityRole="button"
                    accessibilityLabel="Close language selector"
                  >
                    <MaterialIcons name="close" size={20} color="#6b7280" />
                  </TouchableOpacity>
                </View>

                <View className="gap-2">
                  {LANGUAGE_OPTIONS.map((lang) => {
                    const isSelected = lang.code === language;
                    return (
                      <TouchableOpacity
                        key={lang.code}
                        onPress={() => handleLanguageSelect(lang.code)}
                        disabled={isChanging}
                        className={`
                          flex-row items-center justify-between p-4 rounded-lg border-2
                          ${isSelected ? 'bg-primary/10 border-primary' : 'bg-muted border-transparent'}
                          ${isChanging ? 'opacity-50' : ''}
                        `}
                        accessibilityRole="button"
                        accessibilityLabel={`${lang.nativeName} (${lang.name})${isSelected ? ', selected' : ''}`}
                        accessibilityState={{ selected: isSelected }}
                      >
                        <View className="flex-row items-center gap-3">
                          <Text className="text-2xl">{lang.flag}</Text>
                          <View>
                            <Text className="text-base font-semibold text-foreground">
                              {lang.nativeName}
                            </Text>
                            <Text className="text-sm text-muted-foreground">
                              {lang.name}
                            </Text>
                          </View>
                        </View>
                        {isSelected && (
                          <View className="w-6 h-6 items-center justify-center rounded-full bg-primary">
                            <MaterialIcons name="check" size={16} color="white" />
                          </View>
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </View>

                <Text className="text-sm text-muted-foreground text-center mt-4">
                  Your language preference will be saved
                </Text>
              </CardContent>
            </Card>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {feedbackVisible && (
        <View
          pointerEvents="none"
          className="absolute left-0 right-0 items-center"
          style={{
            bottom: 24,
            zIndex: 9999,
          }}
        >
          <View className="px-3 py-2 rounded-md bg-black/80">
            <Text className="text-xs text-white">Language updated</Text>
          </View>
        </View>
      )}
    </>
  );
}
